import React from 'react'
import styled from 'styled-components'
import { useInViewport } from 'react-in-viewport'
import { getCdnPath } from '@utils'


const Picture = ({
  objectFit = 'contain',
  className = '',
  src,
  mobileSrc,
  dimensions,
  alt,
  columns,
  children,
  ...props
}: PictureProps): React.ReactElement => {
  if(!src) return <></>

  const mobileCdnSrc = (!!mobileSrc) ? getCdnPath(mobileSrc) : undefined
  const cdnSrc = getCdnPath(src)

  const ref = React.useRef<HTMLImageElement>(null)
  const { inViewport } = useInViewport( ref, { threshold: .15 } )
  
  // approximate width-of-layout cut by column width
  const desktopCut = (w: number) => !columns ? w : Math.floor(w * (columns / 12))

  return (
      <picture>
        {!!children && children}
        {!children && (
          <>
            {mobileCdnSrc && (
              <source type="image/webp" media="(max-width: 743px)"  srcSet={`${mobileCdnSrc}?fm=webp&w=840`} />
            )}
            {!mobileSrc && (
              <source type="image/webp" media="(max-width: 743px)"  srcSet={`${cdnSrc}?fm=webp&w=840`} />
            )}
            <source type="image/webp" media="(min-width: 1280px)" srcSet={`${cdnSrc}?fm=webp&w=${desktopCut(1200)}, ${cdnSrc}?fm=webp&w=${desktopCut(2400)} 2x`} />
            <source type="image/webp" media="(min-width: 1024px)" srcSet={`${cdnSrc}?fm=webp&w=${desktopCut(1024)}, ${cdnSrc}?fm=webp&w=${desktopCut(2048)} 2x`} />
            <source type="image/webp" media="(min-width: 744px)"  srcSet={`${cdnSrc}?fm=webp&w=${desktopCut(834)},  ${cdnSrc}?fm=webp&w=${desktopCut(1668)} 2x`} />
          </>
        )}

        <StyledImage
          src={`${cdnSrc}?fm=jpg`}
          alt={alt}
          {...{ objectFit, ref, }}
          {...dimensions}
          visible={inViewport}
          className={`loadable ${className}`}
          {...props}
        />
      </picture>
  )
}



// Styling


interface StyledImageProps {
  visible: boolean
  objectFit: 'contain' | 'cover'
  aspectRatio?: number
}

const StyledImage = styled.img.attrs(
  ({ visible }: StyledImageProps) => {
  return { style: {
    opacity: visible ? 1 : 0,
  } }}
)<StyledImageProps>`
  display: block;
  width: 100%;
  height: auto;
  margin: 0;
  padding: 0;
  border: 0;
  object-fit: ${props => props.objectFit};
  transition: opacity 0.5s ease-in-out;
  @media only screen and (min-width: 744px) {
    transition: opacity 0.5s ease-in-out, transform 1.5s ease-in-out;
  }
`



// Typing

type PictureProps = {
  src: string
  mobileSrc?: string
  objectFit?: 'contain' | 'cover'
  dimensions?: {
    width: number
    height: number
    aspectRatio: number
  }
  className?: string
  alt?: string
  columns?: number
  mobileColumns?: number
  children?: React.ReactNode
}



export default Picture
